<template>
    <div class="evaluation-booking-page" :class="{'has-sticky-bar': selectedSlot && !booked}">
        <v-container class="fill-height d-flex align-center justify-center">
            <v-card flat class="booking-card" max-width="680">

                <!-- Success / "You're almost there!" state -->
                <template v-if="booked">
                    <div class="success-state">
                        <div class="success-icon-wrapper mb-4">
                            <div class="success-circle">
                                <v-icon size="48" color="#FFFFFF">mdi-check</v-icon>
                            </div>
                        </div>

                        <div class="booked-chip mb-4">
                            <v-icon size="14" color="#2E7D32" class="mr-1">mdi-check-circle</v-icon>
                            EVALUATION BOOKED
                        </div>

                        <h2 class="success-title mb-3">You're almost there!</h2>
                        <p class="success-subtitle mb-6">
                            Your evaluation is confirmed. Set a password to manage your account, classes, and wallet.
                        </p>

                        <!-- Booking summary card -->
                        <div class="booking-details mb-8" v-if="bookedDateFormatted || bookedTimeFormatted">
                            <div class="detail-row" v-if="bookedDateFormatted">
                                <v-icon size="20" color="#D4780A">mdi-calendar</v-icon>
                                <span>{{ bookedDateFormatted }}</span>
                            </div>
                            <div class="detail-row" v-if="bookedTimeFormatted">
                                <v-icon size="20" color="#D4780A">mdi-clock-outline</v-icon>
                                <span>{{ bookedTimeFormatted }}</span>
                            </div>
                        </div>

                        <!-- Password section -->
                        <div class="password-section">
                            <div class="section-divider mb-6">
                                <span class="divider-label">Secure your account</span>
                            </div>

                            <div class="password-field-wrapper">
                                <p class="password-label mb-3">Create a password</p>
                                <v-text-field
                                    v-model="password"
                                    outlined
                                    placeholder="Enter a password (min. 6 characters)"
                                    :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                                    :type="showPassword ? 'text' : 'password'"
                                    @click:append="showPassword = !showPassword"
                                    hide-details="auto"
                                    class="password-input"
                                />
                            </div>

                            <v-btn
                                block
                                x-large
                                depressed
                                color="#D4780A"
                                class="set-password-btn white--text mt-5"
                                :loading="settingPassword"
                                :disabled="!password || password.length < 6"
                                @click="setInitialPassword"
                            >
                                Set Password & Continue
                            </v-btn>
                        </div>
                    </div>
                </template>

                <!-- Default: Booking State (show dropdowns + calendar) -->
                <template v-else>
                    <div class="card-header">
                        <div class="header-icon">
                            <v-icon size="36" color="#D4780A">mdi-clipboard-check-outline</v-icon>
                        </div>
                        <h1 class="main-title">Book Your Evaluation Session</h1>
                        <p class="main-subtitle">
                            A short 15-minute session with our team to assess your current level and match you with the right course.
                        </p>
                    </div>

                    <div class="card-body">
                        <!-- Date of Birth + Gender -->
                        <v-row class="mb-4">
                            <v-col cols="12" sm="6" class="pb-1">
                                <v-menu
                                    v-model="dobMenu"
                                    :close-on-content-click="false"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="290"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            :value="dobFormatted"
                                            label="Date of Birth"
                                            outlined
                                            readonly
                                            v-bind="attrs"
                                            v-on="on"
                                            hide-details="auto"
                                            append-icon="mdi-calendar"
                                            placeholder="Select date of birth"
                                        />
                                    </template>
                                    <v-date-picker
                                        v-model="dateOfBirth"
                                        :max="maxDob"
                                        :min="minDob"
                                        @input="dobMenu = false"
                                    />
                                </v-menu>
                            </v-col>
                            <v-col cols="12" sm="6" class="pb-1">
                                <v-select
                                    outlined
                                    label="Gender"
                                    placeholder="Select gender"
                                    :items="genderOptions"
                                    v-model="gender"
                                    hide-details="auto"
                                />
                            </v-col>
                        </v-row>

                        <!-- Calendar — always visible -->
                        <evaluation-calendar
                            v-model="selectedSlot"
                            :whatsapp-number="whatsappNumber"
                            @slot-selected="onSlotSelected"
                        />
                    </div>
                </template>
            </v-card>
        </v-container>

        <!-- Sticky confirm bar — always visible once a slot is picked -->
        <transition name="slide-up">
            <div v-if="selectedSlot && !booked" class="sticky-confirm-bar">
                <div class="sticky-confirm-inner">
                    <div class="sticky-slot-info">
                        <div class="sticky-slot-icon">
                            <v-icon size="18" color="#D4780A">mdi-calendar-check</v-icon>
                        </div>
                        <div class="sticky-slot-text">
                            <span class="sticky-slot-date">{{ selectedSlotDate }}</span>
                            <span class="sticky-slot-time">{{ selectedSlotTime }}</span>
                        </div>
                    </div>
                    <v-btn
                        depressed
                        color="#D4780A"
                        class="sticky-confirm-btn white--text"
                        :loading="booking"
                        @click="confirmBooking"
                    >
                        Confirm Booking
                        <v-icon right size="18">mdi-arrow-right</v-icon>
                    </v-btn>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import moment from 'moment-timezone';
import EvaluationCalendar from '@/components/custom/EvaluationCalendar.vue';
import GlobalComputed from '@/helpers/global-computed';
import GlobalMethods from '@/helpers/global-methods';

export default {
    name: 'EvaluationBooking',
    components: {
        EvaluationCalendar
    },
    data() {
        return {
            gender: '',
            dateOfBirth: '',
            dobMenu: false,
            selectedSlot: '',
            slotDetails: null,
            booking: false,
            booked: false,
            bookedData: null,
            // Password step
            password: '',
            showPassword: false,
            settingPassword: false,
        };
    },
    computed: {
        whatsappNumber() {
            return this.appData?.whatsapp_number || '';
        },
        genderOptions() {
            return [
                { text: 'Male', value: 'male' },
                { text: 'Female', value: 'female' },
            ];
        },
        maxDob() {
            return moment().format('YYYY-MM-DD');
        },
        minDob() {
            return moment().subtract(100, 'years').format('YYYY-MM-DD');
        },
        dobFormatted() {
            return this.dateOfBirth ? moment(this.dateOfBirth).format('MMMM D, YYYY') : '';
        },
        bookedDateFormatted() {
            if (!this.bookedData) return '';
            const m = moment.utc(this.bookedData.booked_start).tz(moment.tz.guess());
            return m.format('dddd, MMMM D, YYYY');
        },
        bookedTimeFormatted() {
            if (!this.bookedData) return '';
            const tz = moment.tz.guess();
            const m = moment.utc(this.bookedData.booked_start).tz(tz);
            const end = moment.utc(this.bookedData.booked_end).tz(tz);
            return `${m.format('h:mm A')} – ${end.format('h:mm A')}`;
        },
        selectedSlotDate() {
            if (!this.selectedSlot) return '';
            return moment.utc(this.selectedSlot).tz(moment.tz.guess()).format('ddd, MMM D');
        },
        selectedSlotTime() {
            if (!this.selectedSlot) return '';
            return moment.utc(this.selectedSlot).tz(moment.tz.guess()).format('h:mm A');
        },
        ...GlobalComputed
    },
    methods: {
        onSlotSelected(details) {
            this.slotDetails = details;
        },
        async loadExistingBookingForPasswordStep() {
            try {
                const response = await window.axios.get('/evaluations/my');
                this.bookedData = response.data.evaluation;
                this.booked = true;
            } catch (e) {
                // silently ignore — password step will show without booking details
                this.booked = true;
            }
        },
        async confirmBooking() {
            if (!this.selectedSlot) return;
            if (!this.gender || !this.dateOfBirth) {
                this.showMsg('Please fill in your date of birth and gender to continue.', 'error');
                return;
            }

            this.booking = true;
            try {
                const response = await window.axios.post('/evaluations/book', {
                    booked_start: this.selectedSlot,
                    gender: this.gender,
                    date_of_birth: this.dateOfBirth,
                });
                this.bookedData = response.data;
                this.booked = true;
                this.$store.dispatch('refreshUser');
            } catch (error) {
                const msg = error.response?.data?.message || 'Failed to book evaluation. Please try again.';
                this.showMsg(msg, 'error');
            } finally {
                this.booking = false;
            }
        },
        async setInitialPassword() {
            if (!this.password || this.password.length < 6) {
                this.showMsg('Password must be at least 6 characters', 'error');
                return;
            }
            this.settingPassword = true;
            try {
                await window.axios.post('/set-password', { password: this.password });
                await this.$store.dispatch('refreshUser');
                this.$router.push({ name: 'interests' });
            } catch (e) {
                this.showMsg('Failed to set password. Please try again.', 'error');
            } finally {
                this.settingPassword = false;
            }
        },
        ...GlobalMethods
    },
    mounted() {
        // Pre-fill from user profile if already known
        if (this.user?.gender) this.gender = this.user.gender;
        if (this.user?.date_of_birth) this.dateOfBirth = this.user.date_of_birth;

        // User has booked but hasn't set password yet — show "almost there" state
        if (this.user?.needs_password_set) {
            this.loadExistingBookingForPasswordStep();
            return;
        }
        // Redirect if evaluation is not required for this account
        if (this.user && this.user.evaluation_required === false) {
            this.$router.push({ name: 'home' });
            return;
        }
        // Guard: if user already has an active or completed evaluation, go home
        window.axios.get('/evaluations/my').then(response => {
            if (response.data.has_active_evaluation || response.data.has_completed_evaluation) {
                this.$router.replace({ name: 'home' });
            }
        }).catch(() => {});
    }
};
</script>

<style lang="scss" scoped>
.evaluation-booking-page {
    min-height: 100vh;
    background-color: #FAFAFA;
    padding: 20px 0;

    @media (max-width: 960px) {
        padding: 0;
        background-color: white;
    }
}

.booking-card {
    width: 100%;
    padding: 48px 40px;
    background: white;
    border-radius: 20px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);

    @media (max-width: 960px) {
        padding: 24px 16px 80px;
        border-radius: 0;
        box-shadow: none;
    }
}

.card-header {
    text-align: center;
    margin-bottom: 36px;
}

.header-icon {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    background: #FFF3E0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
}

.main-title {
    font-family: 'Inter', sans-serif;
    font-size: 28px;
    font-weight: 700;
    color: #1E1E1E;
    line-height: 1.3;
    margin-bottom: 8px;

    @media (max-width: 600px) {
        font-size: 24px;
    }
}

.main-subtitle {
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    color: #707070;
    line-height: 1.6;
    max-width: 480px;
    margin: 0 auto;
}

.card-body {
    max-width: 520px;
    margin: 0 auto;
}

.select-prompt {
    text-align: center;
    padding: 32px 0 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.select-prompt-text {
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #BDBDBD;
    margin: 0;
}

.confirm-btn {
    height: 52px !important;
    border-radius: 12px !important;
    font-family: 'Montserrat', sans-serif;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: none;
}

/* Success / "You're almost there!" state */
.success-state {
    text-align: center;
    padding: 20px 0;

    @media (max-width: 600px) {
        padding: 10px 0;
    }
}

.success-icon-wrapper {
    display: flex;
    justify-content: center;
}

.success-circle {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #D4780A, #E79007);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 24px rgba(212, 120, 10, 0.3);
}

.booked-chip {
    display: inline-flex;
    align-items: center;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 600;
    color: #2E7D32;
    letter-spacing: 0.8px;
    text-transform: uppercase;
}

.success-title {
    font-family: 'Inter', sans-serif;
    font-size: 28px;
    font-weight: 700;
    color: #1E1E1E;

    @media (max-width: 600px) {
        font-size: 24px;
    }
}

.success-subtitle {
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    color: #707070;
    line-height: 1.6;
    max-width: 420px;
    margin-left: auto;
    margin-right: auto;
}

.booking-details {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    background: #FFF8F0;
    border: 1px solid #FFE0B2;
    border-radius: 12px;
    padding: 20px 32px;
}

.detail-row {
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    font-weight: 500;
    color: #1E1E1E;
}

.password-section {
    max-width: 400px;
    margin: 0 auto;
    text-align: left;
}

.section-divider {
    display: flex;
    align-items: center;
    gap: 12px;

    &::before,
    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: #E0E0E0;
    }
}

.divider-label {
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    color: #9E9E9E;
    white-space: nowrap;
}

.password-label {
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: #1E1E1E;
    margin: 0;
}

.set-password-btn {
    height: 52px !important;
    border-radius: 12px !important;
    font-family: 'Montserrat', sans-serif;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: none;
}

/* Extra bottom space so the sticky bar doesn't cover content */
.evaluation-booking-page.has-sticky-bar {
    padding-bottom: 88px;
}

/* Sticky confirm bar */
.sticky-confirm-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 200;
    background: #FFFFFF;
    border-top: 1px solid #F0F0F0;
    box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.10);
    padding: 12px 24px;

    @media (max-width: 600px) {
        padding: 10px 16px;
    }
}

.sticky-confirm-inner {
    max-width: 680px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.sticky-slot-info {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.sticky-slot-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: #FFF3E0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.sticky-slot-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.sticky-slot-date {
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #1E1E1E;
    white-space: nowrap;
}

.sticky-slot-time {
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    color: #707070;
    white-space: nowrap;
}

.sticky-confirm-btn {
    height: 44px !important;
    border-radius: 10px !important;
    font-family: 'Montserrat', sans-serif;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: none;
    flex-shrink: 0;
}

/* Slide-up transition */
.slide-up-enter-active,
.slide-up-leave-active {
    transition: transform 0.25s ease, opacity 0.25s ease;
}
.slide-up-enter,
.slide-up-leave-to {
    transform: translateY(100%);
    opacity: 0;
}
</style>
